import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ksh } from "@/lib/api";
import { TRACK } from "@/constants/testIds";
import { Search, CheckCircle2, Clock, Printer, Package, XCircle, AlertCircle } from "lucide-react";

const STATUS_STEPS = [
  { key: "pending_payment", label: "Pending Payment", icon: AlertCircle },
  { key: "paid", label: "Paid", icon: CheckCircle2 },
  { key: "printing", label: "Printing", icon: Printer },
  { key: "ready", label: "Ready for Pickup", icon: Package },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
];

function statusIndex(s) {
  const idx = STATUS_STEPS.findIndex((x) => x.key === s);
  return idx === -1 ? 0 : idx;
}

export default function Track() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("ref") || "";
  const [ref, setRef] = useState(initial);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (refToUse) => {
    const r = refToUse ?? ref;
    if (!r.trim()) return;
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.get(`/orders/track/${r.toUpperCase()}`);
      setOrder(data);
      setParams({ ref: r.toUpperCase() });
    } catch (e) {
      setError("No order found for that tracking number.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (initial) lookup(initial);
  }, []);

  const idx = order ? statusIndex(order.status) : -1;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 lg:py-16">
      <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#525252] mb-3">§ Order Tracking</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-6">
        Where is my order?
      </h1>

      <div className="flex gap-2 mb-8">
        <input
          data-testid={TRACK.input}
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          placeholder="CTP-XXXXXX-XXXXXX"
          className="flex-1 px-4 py-4 border-2 border-[#0A0A0A] focus:border-[#059669] outline-none mono uppercase"
        />
        <button
          data-testid={TRACK.submit}
          onClick={() => lookup()}
          disabled={loading}
          className="px-6 py-4 bg-[#0A0A0A] text-white text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#059669] disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Search className="w-4 h-4" /> Track
        </button>
      </div>

      {error && (
        <div className="border-2 border-[#DC2626] bg-[#DC2626]/5 p-4 mb-6 flex items-center gap-3">
          <XCircle className="w-5 h-5 text-[#DC2626]" />
          <div>{error}</div>
        </div>
      )}

      {order && (
        <div className="space-y-6" data-testid={TRACK.result}>
          <div className="border border-[#0A0A0A] bg-white p-6">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-1">Tracking number</div>
                <div className="mono font-display font-black text-2xl">{order.tracking_number}</div>
                <div className="text-xs text-[#525252] mt-1">Placed {new Date(order.created_at).toLocaleString()}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-1">Total paid</div>
                <div className="mono font-display font-black text-2xl text-[#059669]">{ksh(order.total)}</div>
                <div className="text-xs text-[#525252] mt-1 uppercase tracking-[0.15em]">{order.payment_status}</div>
              </div>
            </div>

            {/* Status timeline */}
            <div className="border-t border-[#E5E5E5] pt-6">
              <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#525252] mb-4">§ Status</div>
              {order.status === "cancelled" ? (
                <div className="border-2 border-[#DC2626] bg-[#DC2626]/5 p-4 flex items-center gap-3">
                  <XCircle className="w-5 h-5 text-[#DC2626]" />
                  <div>Order cancelled.</div>
                </div>
              ) : (
                <div className="space-y-3">
                  {STATUS_STEPS.map((s, i) => {
                    const Icon = s.icon;
                    const passed = i <= idx;
                    const current = i === idx;
                    return (
                      <div key={s.key} className="flex items-center gap-4">
                        <div className={`w-10 h-10 border-2 flex items-center justify-center ${passed ? "border-[#059669] bg-[#059669] text-white" : "border-[#E5E5E5] text-[#525252]"}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className={`text-sm uppercase tracking-[0.15em] font-bold ${passed ? "text-[#0A0A0A]" : "text-[#525252]"}`}>
                            {s.label}
                          </div>
                          {current && <div className="text-xs text-[#059669] mono mt-0.5">Current status</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="border border-[#E5E5E5] bg-white p-6">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#525252] mb-4">§ Items in this order</div>
            <ul className="space-y-3">
              {order.items.map((it, i) => (
                <li key={`${it.file_name || 'item'}-${i}`} className="flex items-start justify-between gap-3 text-sm border-b border-[#E5E5E5] pb-3 last:border-b-0">
                  <div>
                    <div className="font-bold">{it.file_name}</div>
                    <div className="text-xs text-[#525252] mono">
                      {it.copies}× · {it.paper_size} · {it.color_mode === "color" ? "Color" : "B&W"} · {it.pages}pg
                      {it.double_sided && " · 2-sided"}
                      {it.stapling && " · stapled"}
                      {it.binding && it.binding !== "none" && ` · ${it.binding}-bound (${it.binding_cover || "blue"} cover)`}
                      {it.lamination && " · laminated"}
                      {it.batch_copies && ` · batched (${(it.pages || 1) * (it.copies || 1)}pg)`}
                    </div>
                  </div>
                  <div className="mono font-bold">{ksh(it.cost)}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
